/**
 * Format a timestamp for chat / reminder UI in a fixed timezone (default
 * America/Los_Angeles). Stable across reloads — doesn't depend on the
 * device's local clock interpretation.
 *
 * Output:
 *   today      → "3:42 PM"
 *   yesterday  → "Yesterday · 8:15 PM"
 *   this year  → "May 8 · 2:11 PM"
 *   older      → "May 8, 2024 · 2:11 PM"
 */
export function formatChatTimestamp(iso, tz = "America/Los_Angeles") {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";

  const now = new Date();
  // Compare YYYY-MM-DD strings in the same tz so day-rollover is correct.
  const fmtDate = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const fmtTime = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const fmtMonthDay = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    month: "short",
    day: "numeric",
  });
  const fmtMonthDayYear = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const dDay = fmtDate.format(d); // "2026-05-08"
  const todayDay = fmtDate.format(now);
  const yesterday = new Date(now.getTime() - 86400_000);
  const yesterdayDay = fmtDate.format(yesterday);

  const time = fmtTime.format(d);

  if (dDay === todayDay) return time;
  if (dDay === yesterdayDay) return `Yesterday · ${time}`;

  const sameYear = dDay.slice(0, 4) === todayDay.slice(0, 4);
  return sameYear
    ? `${fmtMonthDay.format(d)} · ${time}`
    : `${fmtMonthDayYear.format(d)} · ${time}`;
}
